import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Clock, Paperclip } from "lucide-react";
import { cn } from "@/lib/utils";

interface MessageBubbleProps {
  message: {
    id: string;
    content: string;
    timestamp: string;
    sender: {
      id: string;
      name: string;
      avatar?: string;
    };
    attachments?: Array<{
      name: string;
      url: string;
      type: string;
      size?: number;
    }>;
  };
  currentUserId: string;
  showAvatar?: boolean;
  showTimestamp?: boolean;
  className?: string;
  children?: React.ReactNode; // For context menu wrapper
}

export function MessageBubble({
  message,
  currentUserId,
  showAvatar = true,
  showTimestamp = true,
  className,
  children,
}: MessageBubbleProps) {
  const isCurrentUser = message.sender.id === currentUserId;

  return (
    <div
      className={cn(
        "flex max-w-[80%] gap-3",
        isCurrentUser ? "ml-auto justify-end" : "mr-auto justify-start",
        className
      )}
    >
      {/* Avatar for other users (left side) */}
      {!isCurrentUser && showAvatar && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={message.sender.avatar} />
          <AvatarFallback className="bg-blue-100 text-xs text-blue-600">
            {message.sender.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()}
          </AvatarFallback>
        </Avatar>
      )}

      {/* Message content */}
      <div className={cn("flex flex-col", isCurrentUser ? "items-end" : "items-start")}>
        {/* Sender name for other users */}
        {!isCurrentUser && (
          <span className="mb-1 px-1 text-xs text-gray-500">{message.sender.name}</span>
        )}

        {/* Message bubble */}
        <div
          className={cn(
            "max-w-full break-words rounded-lg px-4 py-2",
            isCurrentUser
              ? "rounded-br-sm bg-blue-600 text-white"
              : "rounded-bl-sm bg-gray-100 text-gray-800"
          )}
        >
          {children ? children : <p className="whitespace-pre-wrap">{message.content}</p>}
        </div>

        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 max-w-full space-y-1">
            {message.attachments.map((attachment, index) => (
              <div
                key={`${message.id}-attachment-${index}`}
                className={cn(
                  "flex items-center gap-2 rounded border p-2 text-sm",
                  isCurrentUser ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-gray-50"
                )}
              >
                <Paperclip className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "truncate hover:underline",
                    isCurrentUser ? "text-blue-700" : "text-gray-700"
                  )}
                >
                  {attachment.name}
                </a>
                {attachment.size && (
                  <span className="ml-auto flex-shrink-0 text-xs text-gray-500">
                    {(attachment.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Timestamp */}
        {showTimestamp && (
          <div
            className={cn(
              "mt-1 flex items-center text-xs text-gray-500",
              isCurrentUser ? "justify-end" : "justify-start"
            )}
          >
            <Clock className="mr-1 h-3 w-3" />
            <span>{message.timestamp}</span>
          </div>
        )}
      </div>

      {/* Avatar for current user (right side) */}
      {isCurrentUser && showAvatar && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={message.sender.avatar} />
          <AvatarFallback className="bg-blue-600 text-xs text-white">
            {message.sender.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
}
